import React from 'react';
import classnames from 'classnames';

import styles from './index.module.less';
import modifiersToClassNames from '../../../core/modifiersToClassNames';

interface GradientProps {
  angle: string;
  colors: string[];
}

interface BackgroundImageProps {
  className?: string;
  children?: React.ReactNode;
  imageUrl: string;
  height?: string | number;
  $minHeight?: string | number;
  qeId?: string;
  $withOverlay?: boolean;
  $withGradient?: boolean | 'dark' | 'light';
  $reverseGradientOrder?: boolean;
  $horizontalPosition?: 'left' | 'center' | 'right';
  $verticalPosition?: 'top' | 'center' | 'bottom';
  $backgroundSize?: string;
  $backgroundColor?: string;
  $backgroundRepeat?:
    | 'repeat'
    | 'repeat-x'
    | 'repeat-y'
    | 'no-repeat'
    | 'inherit';
  $fullWidth?: boolean;
  $gradientProps?: GradientProps;
  title?: string;
}

export const BackgroundImage = ({
  className,
  children,
  imageUrl,
  height,
  qeId = 'BackgroundImage',
  title,
  $withOverlay,
  $withGradient,
  $horizontalPosition = 'center',
  $verticalPosition = 'center',
  $backgroundSize = 'cover',
  $backgroundColor = 'transparent',
  $backgroundRepeat = 'no-repeat',
  $gradientProps,
  $reverseGradientOrder,
  $fullWidth = false,
  $minHeight = 0,
}: BackgroundImageProps): React.ReactElement => {
  if (typeof height === 'number') {
    height = `${height}px`;
  }
  if (typeof $minHeight === 'number') {
    $minHeight = `${$minHeight}px`;
  }

  // Backwards Compatibility: before, $withGradient accepted a boolean (dark),
  // now supports more than one color
  if ($withGradient === true) {
    $withGradient = 'dark';
  }

  const backgroundImageArray = [
    `url('${imageUrl}')`,
    $gradientProps &&
      `linear-gradient(${
        $gradientProps.angle
      }, ${$gradientProps.colors.join()})`,
  ];

  $reverseGradientOrder && backgroundImageArray.reverse();

  return (
    <div
      className={classnames(
        styles.root,
        {
          [styles.fullWidth]: $fullWidth,
        },
        className
      )}
      style={{
        height,
        backgroundImage: backgroundImageArray.filter(Boolean).join(),
        backgroundPosition: `${$horizontalPosition} ${$verticalPosition}`,
        backgroundSize: $backgroundSize,
        backgroundRepeat: $backgroundRepeat,
        backgroundColor: $backgroundColor,
        minHeight: $minHeight,
      }}
      data-qe-id={qeId}
      title={title}
    >
      {$withOverlay && <div className={styles.withOverlay} />}
      {$withGradient && (
        <div
          className={classnames(
            styles.withGradient,
            modifiersToClassNames(styles, { $withGradient })
          )}
        />
      )}
      {children && (
        <div
          className={styles.content}
          style={{
            height,
            minHeight: $minHeight,
          }}
        >
          {children}
        </div>
      )}
    </div>
  );
};
